import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";

/**
 * activePage: "calculator" | "articles" | null
 * Highlights the current page in the nav accordingly.
 */
export default function SharedNavbar({ activePage = null }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const linkBase = "text-base font-medium text-slate-600 hover:text-slate-900 transition-colors";
  const activeLink = "text-base font-semibold text-blue-600";

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{
        background: scrolled ? "rgba(255,255,255,0.97)" : "rgba(255,255,255,0.88)",
        backdropFilter: "blur(16px)",
        borderBottom: scrolled ? "1px solid #e2e8f0" : "1px solid transparent",
        boxShadow: scrolled ? "0 1px 20px rgba(0,0,0,0.06)" : "none",
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">

          {/* Logo */}
          <a href="/" data-testid="nav-logo" className="flex items-center gap-2.5">
            <img
              src="/rvu-logo.png"
              alt="Doc RVU Tracker"
              style={{ width: 42, height: 42, borderRadius: "11px", objectFit: "cover", boxShadow: "0 2px 8px rgba(22,163,74,0.30)" }}
            />
            <span style={{ fontSize: 20, fontWeight: 700, color: "#0f172a", letterSpacing: "-0.5px", fontFamily: "Inter, sans-serif" }}>
              Doc RVU Tracker
            </span>
          </a>

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center gap-7">
            <a href="/#features" className={linkBase} data-testid="nav-features">Features</a>
            <a href="/#how-it-works" className={linkBase} data-testid="nav-how-it-works">How It Works</a>
            <a href="/#pricing" className={linkBase} data-testid="nav-pricing">Pricing</a>
            <a
              href="/rvu-articles"
              className={activePage === "articles" ? activeLink : linkBase}
              data-testid="nav-articles"
            >
              Articles
            </a>
            {activePage === "calculator" ? (
              <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-3 py-1.5 rounded-full border border-blue-200">
                Free wRVU Calculator
              </span>
            ) : (
              <a
                href="/free-rvu-calculator"
                className="text-xs font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-full border border-blue-200 transition-colors"
                data-testid="nav-calculator"
              >
                Free wRVU Calculator
              </a>
            )}
          </div>

          {/* Desktop CTA buttons */}
          <div className="hidden md:flex items-center gap-4">
            <a
              href="/login"
              className="text-base font-medium text-slate-700 hover:text-blue-600 transition-colors px-4 py-2"
              data-testid="nav-login-btn"
            >
              Log In
            </a>
            <a
              href="/register"
              className="text-base font-semibold text-white rounded-full px-6 py-2.5 transition-all duration-200 hover:-translate-y-px hover:shadow-lg"
              style={{ background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)", boxShadow: "0 2px 12px rgba(37,99,235,0.30)" }}
              data-testid="nav-register-btn"
            >
              Get Started for Free
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden p-2 rounded-lg text-slate-600 hover:bg-slate-100"
            onClick={() => setMenuOpen(!menuOpen)}
            data-testid="nav-mobile-menu"
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-slate-100 px-4 py-4 space-y-3">
          <a href="/#features" className="block text-sm font-medium text-slate-700 py-2">Features</a>
          <a href="/#how-it-works" className="block text-sm font-medium text-slate-700 py-2">How It Works</a>
          <a href="/#pricing" className="block text-sm font-medium text-slate-700 py-2">Pricing</a>
          <a
            href="/rvu-articles"
            className={`block text-sm py-2 ${activePage === "articles" ? "font-semibold text-blue-600" : "font-medium text-slate-700"}`}
          >
            Articles
          </a>
          <a
            href="/free-rvu-calculator"
            className={`block text-sm py-2 ${activePage === "calculator" ? "font-semibold text-blue-600" : "font-medium text-slate-700"}`}
          >
            Free wRVU Calculator
          </a>
          <div className="pt-3 border-t border-slate-100 flex flex-col gap-2">
            <a href="/login" className="text-center py-2 text-sm font-medium text-slate-700 border border-slate-200 rounded-full">
              Log In
            </a>
            <a
              href="/register"
              className="text-center py-2.5 text-sm font-semibold text-white rounded-full"
              style={{ background: "linear-gradient(135deg, #2563eb, #1d4ed8)" }}
            >
              Get Started for Free
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}
